import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  supplierListPageQuery,
  supplierDelete as supplierDeleteMutation,
} from '../graphql/supplier_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const SupplierList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [supplierDelete] = useMutation(supplierDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(supplierListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ supplierList }) => {
      // write an individual query for every supplier in the list
      updateFunctions.supplierList(pageClient, supplierList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const supplierDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const supplierId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: supplierId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'SupplierType',
        recordId: supplierId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.supplierDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'supplierDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await supplierDelete(mutationData);
        toastSuccess('Supplier delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Supplier delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      supplierDelete(mutationData);
      toastWarning(`Supplier delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const parentColumns = [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Suppliers</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/suppliers/new">
                <Button variant="primary">New Supplier</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="supplier"
                parentColumns={parentColumns}
                data={pageData.supplierList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={supplierDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default SupplierList;
